import {
  type LivestockEventRecordT,
  type LivestockEventTypeT,
} from '@ruminati/types_v2/livestock_events'
import {
  type LivestockMobPurposeT,
  type LivestockMobRecordT,
} from '@ruminati/types_v2/livestock_mobs'
import { type LivestockGenderT, type LivestockTagColorT } from '@ruminati/types_v2/livestock_shared'
import {
  type CattleBreedConfigT,
  type SheepBreedConfigT,
} from '@ruminati/types_v2/livestock_system'
import dayjs from 'dayjs'
import { flat, mergeDeep, clone, capitalize } from 'remeda'

import { CattleAgeClass, getCattleGenderAgeClassLabel } from './age-classes-cattle'
import { SheepAgeClass, getSheepGenderAgeClassLabel } from './age-classes-sheep'
import { type HeadcountEvent, type WeighingEvent, type LocationEvent } from './events'
import { getRatesForDate } from './valuation-rates-quarterly'
import {
  calculateDailyGrowth,
  getGrowthCurveConfig,
  getSeasonalConfig,
  type GrowthCurveConfig,
  type MarketCondition,
  type SeasonalConfig,
} from './weight'

export type LifeCycleEvents = {
  weaned?: Date

  // Female events
  firstJoined?: Date

  // Male events
  castrated?: Date
}

export type AgeClassPeriod<SpeciesAgeClass extends CattleAgeClass | SheepAgeClass> = {
  ageClass: SpeciesAgeClass
  startDate: Date
  endDate: Date | null // null means "ongoing" (e.g., COW_MATURE has no end)
  ageInMonthsAtStart: number
  ageInMonthsAtEnd: number | null
  note?: string // Optional: explain why this period exists
}

// A mob is the primary livestock entity: it carries its own biological identity
// (species/gender/birth date/breed configuration) and its own headcount/location
// history - it no longer borrows any of this from a parent cohort. "Cohort" survives
// only as a computed grouping of mobs that share these attributes (see cohort.ts).
type BaseMobT = {
  id: string

  birthDate: Date
  gender: LivestockGenderT
  tags?: LivestockTagColorT[]
  name?: string

  purpose?: LivestockMobPurposeT
  eventHistory: {
    lifecycleEventsKnown: LifeCycleEvents
    lifecycleEventsPredicted: LifeCycleEvents
    headcountEvents: HeadcountEvent[]
    weighingEvents: WeighingEvent[]
  }
  locationEvents: LocationEvent[]
  closedAt?: Date

  growthCurveConfig: GrowthCurveConfig
  seasonalConfig: SeasonalConfig

  projectedGrowthCurveAverage?: GrowthCurvePoint[]
  projectedGrowthCurveAverageLatestWeighingDate?: Date | null
  expectedAgeClassProgression: AgeClassPeriod<CattleAgeClass | SheepAgeClass>[]

  records: {
    mob: LivestockMobRecordT
    events: LivestockEventRecordT[]
  }
}

export type CattleMobT = BaseMobT & {
  species: 'cattle'
  systemDetails: CattleBreedConfigT
  expectedAgeClassProgression: AgeClassPeriod<CattleAgeClass>[]
}

export type SheepMobT = BaseMobT & {
  species: 'sheep'
  systemDetails: SheepBreedConfigT
  expectedAgeClassProgression: AgeClassPeriod<SheepAgeClass>[]
}

export type MobT = CattleMobT | SheepMobT

export type NewMobT = Omit<MobT, 'records'> & {
  currentAgeClass: CattleAgeClass | SheepAgeClass
  notes: string
}

function parseHeadcountEventFromRecord(
  mobId: string,
  event: LivestockEventRecordT,
): HeadcountEvent[] {
  return event.details
    .filter((d) => d.mobId === mobId && d.headcountChange)
    .map((d) => {
      return {
        eventId: event.id,
        date: dayjs(event.eventDate).toDate(),
        headcountChange: d.headcountChange!,
        type: event.type,
      }
    })
}

function parseWeightEventFromRecord(mobId: string, event: LivestockEventRecordT): WeighingEvent[] {
  return event.details
    .filter((d) => d.mobId === mobId && d.livestockSnapshot === undefined)
    .filter(
      (d) =>
        (event.metadata &&
          'avgWeightKg' in event.metadata &&
          typeof event.metadata.avgWeightKg === 'number') ||
        d.avgWeightKg !== undefined,
    )
    .map((d) => {
      const weight =
        d.avgWeightKg ??
        (event.metadata && 'avgWeightKg' in event.metadata ? event.metadata.avgWeightKg : undefined)
      return {
        eventId: event.id,
        date: dayjs(event.eventDate).toDate(),
        averageWeight: weight!,
      }
    })
}

function parseLocationEventFromRecord(
  mobId: string,
  event: LivestockEventRecordT,
): LocationEvent[] {
  return event.details
    .filter((d) => d.mobId === mobId && d.paddockId)
    .map((d) => ({
      eventId: event.id,
      date: dayjs(event.eventDate).toDate(),
      paddockId: d.paddockId!,
      type: event.type,
    }))
}

export function createMobFromRecords(options: {
  mobRecord: LivestockMobRecordT
  mobEvents: LivestockEventRecordT[]
}): MobT {
  let lifecycleEventsKnown: LifeCycleEvents = {}
  if (options.mobRecord.weaned)
    lifecycleEventsKnown.weaned = dayjs(options.mobRecord.weaned).toDate()
  if (options.mobRecord.firstJoined)
    lifecycleEventsKnown.firstJoined = dayjs(options.mobRecord.firstJoined).toDate()
  if (options.mobRecord.castrated)
    lifecycleEventsKnown.castrated = dayjs(options.mobRecord.castrated).toDate()

  const sortedEvents = [...options.mobEvents].sort((a, b) => {
    if (dayjs(a.eventDate).isBefore(b.eventDate)) return -1
    return 1
  })
  const weighingEvents = flat(
    sortedEvents.map((e) => parseWeightEventFromRecord(options.mobRecord.id, e)),
  )
  const assertedWeightDate = options.mobRecord.averageWeightRecordedAt
  if (
    options.mobRecord.averageWeightKg !== null &&
    assertedWeightDate !== null &&
    !weighingEvents.some(
      (event) =>
        event.averageWeight === options.mobRecord.averageWeightKg &&
        dayjs(event.date).isSame(dayjs(assertedWeightDate), 'day'),
    )
  ) {
    weighingEvents.push({
      eventId: `mob-asserted-weight:${options.mobRecord.id}`,
      date: dayjs(assertedWeightDate).toDate(),
      averageWeight: options.mobRecord.averageWeightKg,
    })
  }

  const systemDetails = options.mobRecord.metadata.configuration

  const mob = {
    id: options.mobRecord.id,
    gender: options.mobRecord.gender,
    species: options.mobRecord.species,
    name: options.mobRecord.name ?? undefined,
    tags: options.mobRecord.tags ?? undefined,
    birthDate: dayjs(options.mobRecord.birthYearMonth).toDate(),
    purpose: options.mobRecord.metadata?.purpose ?? undefined,
    closedAt: options.mobRecord.closedAt ? dayjs(options.mobRecord.closedAt).toDate() : undefined,
    eventHistory: {
      lifecycleEventsKnown: lifecycleEventsKnown,
      // placeholder to be computed later
      lifecycleEventsPredicted: {},
      headcountEvents: flat(
        sortedEvents.map((e) => parseHeadcountEventFromRecord(options.mobRecord.id, e)),
      ),
      weighingEvents,
    },
    locationEvents: flat(
      sortedEvents.map((e) => parseLocationEventFromRecord(options.mobRecord.id, e)),
    ),

    systemDetails,
    growthCurveConfig: getGrowthCurveConfig(systemDetails),
    seasonalConfig: getSeasonalConfig(systemDetails.state),
    // placeholder to be computed later
    expectedAgeClassProgression: [],

    records: {
      mob: options.mobRecord,
      events: options.mobEvents,
    },
  } as MobT

  mob.eventHistory.lifecycleEventsPredicted = projectLifeCycleEventsForMob(mob)

  if (mob.species === 'cattle') {
    mob.expectedAgeClassProgression = computeExpectedAgeClassProgression<CattleAgeClass>(mob)
  } else {
    mob.expectedAgeClassProgression = computeExpectedAgeClassProgression<SheepAgeClass>(mob)
  }

  const growthCurveEnd = dayjs().add(4, 'years')
  const latestWeighingDate = mob.eventHistory.weighingEvents.reduce<Date | null>(
    (latest, event) => {
      if (!latest) return event.date
      return dayjs(event.date).isAfter(latest) ? event.date : latest
    },
    null,
  )
  mob.projectedGrowthCurveAverageLatestWeighingDate = latestWeighingDate
  mob.projectedGrowthCurveAverage = computeProjectedGrowthCurve(
    mob,
    growthCurveEnd.toDate(),
    'average',
  )

  return mob
}

// takes a partial because some things might be absent during mob creation...
export function computeMobName(
  mob: Partial<MobT>,
  options?: {
    includeBreed?: boolean
  },
) {
  if (mob.name) return mob.name

  let out = ''
  if (mob.birthDate) {
    out = `${dayjs(mob.birthDate).format("MMM 'YY")}`
  }
  if (mob.gender) out = `${out} ${capitalize(mob.gender)}${mob.gender !== 'mixed' ? 's' : ''}`
  if (mob.tags) out = `${out} ${mob.tags.join('/')} Tags`
  if (options && options.includeBreed) out = `${out} - ${mob.systemDetails?.breed}`

  return out.trim()
}

export function projectLifeCycleEventsForMob(mob: MobT): LifeCycleEvents {
  const birthDate = dayjs(mob.birthDate)

  let out: LifeCycleEvents = {
    weaned: birthDate.add(mob.systemDetails.weaningAgeInMonths, 'month').toDate(),
  }

  if (mob.gender === 'female') {
    out.firstJoined = birthDate.add(mob.systemDetails.joiningAgeInMonths, 'month').toDate()
  }

  if (mob.gender === 'male') {
    out.castrated = dayjs(mob.birthDate).add(6, 'month').toDate()
  }

  return out
}

export function getMobAgeClassLabel(mob: MobT, options?: { date: Date }) {
  const ageClass = computeAgeClass(mob, options?.date ?? new Date())
  if (mob.species === 'cattle') {
    return getCattleGenderAgeClassLabel(mob.gender, ageClass as CattleAgeClass)
  }
  return getSheepGenderAgeClassLabel(mob.gender, ageClass as SheepAgeClass)
}

export function addNewMobHeadcountEventFromRecord(
  mob: MobT,
  eventRecord: LivestockEventRecordT,
): void {
  const parsedEvents = parseHeadcountEventFromRecord(mob.id, eventRecord)
  mob.eventHistory.headcountEvents.push(...parsedEvents)

  mob.records.events.push(eventRecord)
}

export type MobLocation = {
  paddockId: string
  since: Date
}

// The most recent location event on or before `atDate` - a mob's current
// paddock as of that date, not just "whatever's latest right now".
export function computeMobLocationAtDate(mob: MobT, atDate: Date): MobLocation | undefined {
  const sortedEvents = [...mob.locationEvents].sort((a, b) => a.date.getTime() - b.date.getTime())

  let current: MobLocation | undefined
  for (const event of sortedEvents) {
    if (event.date > atDate) break
    current = { paddockId: event.paddockId, since: event.date }
  }

  return current
}

export type ProjectedMobState = {
  mob: MobT
  projectionDate: Date
  projectedState: {
    ageClass: CattleAgeClass | SheepAgeClass
    ageInMonths: number
    headcount: number
    averageLiveweight: number // kg
    valuationRatePerKgLwt: number | null // $/kg lwt used for valuation; null if no market data for this date
    estimatedValue: number | null
    estimatedValuePerHead: number | null
    currentPaddockId: string | undefined
    currentPaddockSince: Date | undefined
  }
}

export function computeMobStateAtDate(options: {
  mob: MobT
  date: Date
  marketCondition?: MarketCondition
  // Current reads use the authoritative fields on livestock_mobs; historical/as-of reads retain
  // the event projection, using the current row as the baseline for mobs without a founding event.
  useAuthoritativeCurrentState?: boolean
}): ProjectedMobState {
  // New mob creation stores current state directly and deliberately creates no founding event.
  // Keep the historical event projection for legacy/founding-event mobs; use the row baseline for
  // eventless authoritative mobs.
  const projectedHeadcount = options.useAuthoritativeCurrentState
    ? options.mob.records.mob.headcount
    : options.mob.eventHistory.headcountEvents.length > 0
      ? computeMobHeadcountAtDate(options.mob, options.date)
      : options.mob.records.mob.headcount
  const projectedAgeClass = computeAgeClass(options.mob, options.date)
  const projectedAvgLiveweight = projectMobWeight(
    options.mob,
    options.date,
    options.marketCondition,
  )
  const location = options.useAuthoritativeCurrentState
    ? undefined
    : computeMobLocationAtDate(options.mob, options.date)
  const currentPaddockId = options.useAuthoritativeCurrentState
    ? (options.mob.records.mob.paddockId ?? undefined)
    : (location?.paddockId ??
      (options.mob.locationEvents.length === 0
        ? (options.mob.records.mob.paddockId ?? undefined)
        : undefined))
  const currentPaddockSince = options.useAuthoritativeCurrentState
    ? options.mob.records.mob.locationUpdatedAt
      ? dayjs(options.mob.records.mob.locationUpdatedAt).toDate()
      : undefined
    : (location?.since ??
      (options.mob.locationEvents.length === 0 && options.mob.records.mob.locationUpdatedAt
        ? dayjs(options.mob.records.mob.locationUpdatedAt).toDate()
        : undefined))
  const valuationRates = getRatesForDate(options.date)?.[options.mob.species] ?? null
  const valuationRatePerKgLwt = valuationRates
    ? ((valuationRates as Record<string, number>)[projectedAgeClass] ?? null)
    : null

  return {
    mob: options.mob,
    projectionDate: options.date,
    projectedState: {
      ageClass: projectedAgeClass,
      ageInMonths: computeAgeInMonths(options.mob, options.date),
      headcount: projectedHeadcount,
      averageLiveweight: projectedAvgLiveweight,
      valuationRatePerKgLwt,
      estimatedValue:
        valuationRates === null
          ? null
          : computeValue({
              valuationRates,
              ageClass: projectedAgeClass,
              averageLiveweight: projectedAvgLiveweight,
              headcount: projectedHeadcount,
            }),
      estimatedValuePerHead:
        valuationRates === null
          ? null
          : computeValuePerHead({
              valuationRates,
              ageClass: projectedAgeClass,
              averageLiveweight: projectedAvgLiveweight,
            }),
      currentPaddockId,
      currentPaddockSince,
    },
  }
}

export function projectMobWeight(
  mob: MobT,
  dateUntil: Date,
  marketCondition?: MarketCondition,
): number {
  const growthCurve = computeProjectedGrowthCurve(mob, dateUntil, marketCondition)
  return growthCurve.at(growthCurve.length - 1)?.weight ?? 0
}

function computeValue(options: {
  valuationRates: Record<string, number>
  ageClass: CattleAgeClass | SheepAgeClass
  averageLiveweight: number
  headcount: number
}) {
  const pricePerKg = options.valuationRates[options.ageClass] ?? 0
  return options.headcount * options.averageLiveweight * pricePerKg
}

function computeValuePerHead(options: {
  valuationRates: Record<string, number>
  ageClass: CattleAgeClass | SheepAgeClass
  averageLiveweight: number
}) {
  const pricePerKg = options.valuationRates[options.ageClass] ?? 0
  return options.averageLiveweight * pricePerKg
}

function computeAgeInMonths(mob: MobT, atDate: Date): number {
  return Math.abs(dayjs(atDate).diff(dayjs(mob.birthDate), 'month'))
}

export function computeMobHeadcountAtDate(mob: MobT, atDate: Date): number {
  const sortedHeadcountEvents = [...mob.eventHistory.headcountEvents].sort(
    (a, b) => a.date.getTime() - b.date.getTime(),
  )

  // Legacy mobs have an explicit founding event, so their historical baseline is still the event
  // stream. New mobs have no synthetic founding event: use the authoritative current row as the
  // baseline and reverse/forward the known event deltas around the requested date.
  if (sortedHeadcountEvents.some((event) => event.type === 'initial-inventory')) {
    return sortedHeadcountEvents.reduce(
      (headcount, event) => (event.date <= atDate ? headcount + event.headcountChange : headcount),
      0,
    )
  }

  const now = new Date()
  let projectedHeadcount = mob.records.mob.headcount
  for (const event of sortedHeadcountEvents) {
    if (event.date > now && event.date <= atDate) {
      projectedHeadcount += event.headcountChange
    } else if (event.date > atDate && event.date <= now) {
      projectedHeadcount -= event.headcountChange
    }
  }

  // Negative established counts are intentional: they preserve an overdrawn farmer fact instead
  // of silently converting a recorded movement into a different inventory history.
  return projectedHeadcount
}

// A Dry Sheep Equivalent (DSE) is the standard AU grazing-management unit - one DSE is the feed
// requirement of a 50kg dry sheep at maintenance. Other classes of stock scale by metabolic
// liveweight (LW^0.75) relative to that reference, the standard simplification used in state ag
// department carrying-capacity guides (e.g. NSW DPI's pasture calculators) - this is a
// maintenance-only approximation and doesn't add the extra intake pregnancy/lactation/growth
// require, so it will under-read slightly for breeding/growing stock.
const DSE_REFERENCE_LIVEWEIGHT_KG = 50

function dsePerHeadForLiveweight(liveweightKg: number): number {
  return Math.pow(liveweightKg / DSE_REFERENCE_LIVEWEIGHT_KG, 0.75)
}

export function computeMobDSEAtDate(
  mob: MobT,
  atDate: Date,
  marketCondition?: MarketCondition,
): number {
  const headcount = computeMobHeadcountAtDate(mob, atDate)
  const averageLiveweight = projectMobWeight(mob, atDate, marketCondition)
  return headcount * dsePerHeadForLiveweight(averageLiveweight)
}

export function computeExpectedAgeClassProgression<
  SpeciesAgeClass extends CattleAgeClass | SheepAgeClass,
>(mob: MobT): AgeClassPeriod<SpeciesAgeClass>[] {
  if (mob.species === 'cattle') {
    return computeCattleAgeClassProgression(mob) as AgeClassPeriod<SpeciesAgeClass>[]
  }
  return computeSheepAgeClassProgression(mob) as AgeClassPeriod<SpeciesAgeClass>[]
}

function computeCattleAgeClassProgression(mob: CattleMobT): AgeClassPeriod<CattleAgeClass>[] {
  const progression: AgeClassPeriod<CattleAgeClass>[] = []

  // Merge known and predicted lifecycle events
  const lifecycleEvents = mergeDeep(
    clone(mob.eventHistory.lifecycleEventsKnown),
    mob.eventHistory.lifecycleEventsPredicted,
  )

  const birthDate = dayjs(mob.birthDate)

  // Calculate key lifecycle dates
  const weaningDate = lifecycleEvents.weaned
    ? dayjs(lifecycleEvents.weaned)
    : birthDate.add(mob.systemDetails.weaningAgeInMonths, 'months')

  // CALF_UNWEANED: Birth to weaning
  progression.push({
    ageClass: CattleAgeClass.CALF_UNWEANED,
    startDate: birthDate.toDate(),
    endDate: weaningDate.toDate(),
    ageInMonthsAtStart: 0,
    ageInMonthsAtEnd: mob.systemDetails.weaningAgeInMonths,
    note: 'On mother, rapid growth from milk + pasture',
  })

  // Different progression for males vs females
  if (mob.gender === 'male') {
    return computeCattleMaleProgression(mob, progression, weaningDate, lifecycleEvents)
  } else {
    return computeCattleFemaleProgression(mob, progression, weaningDate, lifecycleEvents)
  }
}

function computeCattleMaleProgression(
  mob: MobT,
  progression: AgeClassPeriod<CattleAgeClass>[],
  weaningDate: dayjs.Dayjs,
  lifecycleEvents: LifeCycleEvents,
): AgeClassPeriod<CattleAgeClass>[] {
  const birthDate = dayjs(mob.birthDate)

  const isCastrated = !!lifecycleEvents.castrated

  if (isCastrated) {
    // WEANERS: Weaning to 12 months
    const weanerEnd = birthDate.add(12, 'months')
    progression.push({
      ageClass: CattleAgeClass.WEANERS,
      startDate: weaningDate.toDate(),
      endDate: weanerEnd.toDate(),
      ageInMonthsAtStart: mob.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Post-weaning growth phase, castrated',
    })

    // YEARLINGS: 12-18 months
    const yearlingEnd = birthDate.add(18, 'months')
    progression.push({
      ageClass: CattleAgeClass.YEARLINGS,
      startDate: weanerEnd.toDate(),
      endDate: yearlingEnd.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: 18,
      note: 'Backgrounding phase',
    })

    // STEER_18_24M: 18-24 months
    const steer18_24End = birthDate.add(24, 'months')
    progression.push({
      ageClass: CattleAgeClass.STEER_18_24M,
      startDate: yearlingEnd.toDate(),
      endDate: steer18_24End.toDate(),
      ageInMonthsAtStart: 18,
      ageInMonthsAtEnd: 24,
      note: 'Prime finishing age, often sold',
    })

    // STEER_24_36M: 24-36+ months
    progression.push({
      ageClass: CattleAgeClass.STEER_24_36M,
      startDate: steer18_24End.toDate(),
      endDate: null, // Ongoing
      ageInMonthsAtStart: 24,
      ageInMonthsAtEnd: null,
      note: 'Heavy steer, premium grassfed market (if retained)',
    })
  } else {
    // BULL progression (not castrated)
    const weanerEnd = birthDate.add(12, 'months')
    progression.push({
      ageClass: CattleAgeClass.WEANERS,
      startDate: weaningDate.toDate(),
      endDate: weanerEnd.toDate(),
      ageInMonthsAtStart: mob.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Post-weaning, entire male (not castrated)',
    })

    // BULL_BREEDING: 12+ months (or whenever they enter breeding herd)
    progression.push({
      ageClass: CattleAgeClass.BULL_BREEDING,
      startDate: weanerEnd.toDate(),
      endDate: null,
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Breeding bull or growing entire male',
    })
  }

  return progression
}

function computeCattleFemaleProgression(
  mob: MobT,
  progression: AgeClassPeriod<CattleAgeClass>[],
  weaningDate: dayjs.Dayjs,
  lifecycleEvents: LifeCycleEvents,
): AgeClassPeriod<CattleAgeClass>[] {
  const birthDate = dayjs(mob.birthDate)

  // WEANERS: Weaning to 12 months
  const weanerEnd = birthDate.add(12, 'months')
  progression.push({
    ageClass: CattleAgeClass.WEANERS,
    startDate: weaningDate.toDate(),
    endDate: weanerEnd.toDate(),
    ageInMonthsAtStart: mob.systemDetails.weaningAgeInMonths,
    ageInMonthsAtEnd: 12,
    note:
      mob.purpose === 'breeding'
        ? 'Post-weaning growth, replacement heifer potential'
        : 'Post-weaning growth, production animal',
  })

  // If production purpose, stop at yearlings (won't be joined)
  if (mob.purpose === 'selling') {
    progression.push({
      ageClass: CattleAgeClass.YEARLINGS,
      startDate: weanerEnd.toDate(),
      endDate: null, // Continues until sale
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Growing for sale, not for breeding',
    })
    return progression
  }

  // Determine joining date
  const joiningAge = mob.systemDetails.joiningAgeInMonths
  const joiningDate = lifecycleEvents.firstJoined
    ? dayjs(lifecycleEvents.firstJoined)
    : birthDate.add(joiningAge, 'months')

  // YEARLINGS: 12 months to joining (or 24 months if not joined)
  const yearlingEnd = joiningDate.isBefore(birthDate.add(24, 'months'))
    ? joiningDate
    : birthDate.add(24, 'months')

  progression.push({
    ageClass: CattleAgeClass.YEARLINGS,
    startDate: weanerEnd.toDate(),
    endDate: yearlingEnd.toDate(),
    ageInMonthsAtStart: 12,
    ageInMonthsAtEnd: yearlingEnd.diff(birthDate, 'months'),
    note:
      joiningAge < 24
        ? `Growing to joining age (${joiningAge} months)`
        : 'Growing phase, not yet joined',
  })

  // If joining age is 24+, add HEIFER classes
  if (joiningAge >= 24) {
    // HEIFER_24_30M
    if (joiningAge < 30) {
      const heifer24_30End = joiningDate
      progression.push({
        ageClass: CattleAgeClass.HEIFER_24_30M,
        startDate: birthDate.add(24, 'months').toDate(),
        endDate: heifer24_30End.toDate(),
        ageInMonthsAtStart: 24,
        ageInMonthsAtEnd: heifer24_30End.diff(birthDate, 'months'),
        note: `Unmated heifer, approaching joining age (${joiningAge}mo)`,
      })
    } else {
      // Full 24-30 month period
      const heifer24_30End = birthDate.add(30, 'months')
      progression.push({
        ageClass: CattleAgeClass.HEIFER_24_30M,
        startDate: birthDate.add(24, 'months').toDate(),
        endDate: heifer24_30End.toDate(),
        ageInMonthsAtStart: 24,
        ageInMonthsAtEnd: 30,
        note: 'Unmated heifer, common in tropical systems',
      })

      // HEIFER_30_36M
      if (joiningAge >= 30) {
        const heifer30_36End = joiningDate
        progression.push({
          ageClass: CattleAgeClass.HEIFER_30_36M,
          startDate: heifer24_30End.toDate(),
          endDate: heifer30_36End.toDate(),
          ageInMonthsAtStart: 30,
          ageInMonthsAtEnd: heifer30_36End.diff(birthDate, 'months'),
          note: `Late-joining heifer system (${joiningAge}mo joining)`,
        })
      }
    }
  }

  // Calculate calving dates
  const gestationMonths = 9
  const weaningMonths = 6
  const rebreedingMonths = 3 // Time to reconceive after weaning
  const calvingInterval = weaningMonths + rebreedingMonths + gestationMonths // = 18 months

  const firstCalvingDate = joiningDate.add(gestationMonths, 'months')
  const secondCalvingDate = firstCalvingDate.add(calvingInterval, 'months')

  // If joined before first calving - might be pregnant heifer
  if (joiningDate.isBefore(firstCalvingDate)) {
    const pregnantHeiferStart = joiningDate
    const pregnantHeiferEnd = firstCalvingDate

    // Determine which heifer class based on age at joining
    const ageAtJoining = joiningDate.diff(birthDate, 'months')
    let pregnantClass: CattleAgeClass

    if (ageAtJoining < 24) {
      pregnantClass = CattleAgeClass.YEARLINGS
    } else if (ageAtJoining < 30) {
      pregnantClass = CattleAgeClass.HEIFER_24_30M
    } else {
      pregnantClass = CattleAgeClass.HEIFER_30_36M
    }

    // Only add if we haven't already covered this period
    const lastPeriod = progression[progression.length - 1]
    if (dayjs(lastPeriod?.endDate).isBefore(pregnantHeiferEnd)) {
      progression.push({
        ageClass: pregnantClass,
        startDate: pregnantHeiferStart.toDate(),
        endDate: pregnantHeiferEnd.toDate(),
        ageInMonthsAtStart: ageAtJoining,
        ageInMonthsAtEnd: firstCalvingDate.diff(birthDate, 'months'),
        note: 'Pregnant with first calf',
      })
    }
  }

  // COW_FIRST_CALF: First calving to second calving
  progression.push({
    ageClass: CattleAgeClass.COW_FIRST_CALF,
    startDate: firstCalvingDate.toDate(),
    endDate: secondCalvingDate.toDate(),
    ageInMonthsAtStart: firstCalvingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: secondCalvingDate.diff(birthDate, 'months'),
    note: 'First-calf heifer, lactating and growing, needs to reconceive',
  })

  // COW_MATURE: After second calving
  progression.push({
    ageClass: CattleAgeClass.COW_MATURE,
    startDate: secondCalvingDate.toDate(),
    endDate: null, // Ongoing
    ageInMonthsAtStart: secondCalvingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: null,
    note: 'Mature breeding cow, proven breeder',
  })

  return progression
}

export function computeSheepAgeClassProgression(sheep: SheepMobT): AgeClassPeriod<SheepAgeClass>[] {
  const progression: AgeClassPeriod<SheepAgeClass>[] = []
  const birthDate = dayjs(sheep.birthDate)

  // Merge lifecycle events
  const events = {
    ...sheep.eventHistory.lifecycleEventsKnown,
    ...sheep.eventHistory.lifecycleEventsPredicted,
  }

  const weaningDate = events.weaned
    ? dayjs(events.weaned)
    : birthDate.add(sheep.systemDetails.weaningAgeInMonths, 'months')

  // LAMB_UNWEANED: Birth to weaning
  progression.push({
    ageClass: SheepAgeClass.LAMB_UNWEANED,
    startDate: birthDate.toDate(),
    endDate: weaningDate.toDate(),
    ageInMonthsAtStart: 0,
    ageInMonthsAtEnd: sheep.systemDetails.weaningAgeInMonths,
    note: 'On mother, high growth rate',
  })

  if (sheep.gender === 'male') {
    return computeSheepMaleProgression(sheep, progression, weaningDate, events)
  } else {
    return computeSheepFemaleProgression(sheep, progression, weaningDate, events)
  }
}

function computeSheepMaleProgression(
  sheep: SheepMobT,
  progression: AgeClassPeriod<SheepAgeClass>[],
  weaningDate: dayjs.Dayjs,
  events: LifeCycleEvents,
): AgeClassPeriod<SheepAgeClass>[] {
  const birthDate = dayjs(sheep.birthDate)
  const isCastrated = !!events.castrated

  if (isCastrated) {
    // Wethers
    const hoggetStart = birthDate.add(12, 'months')

    progression.push({
      ageClass: SheepAgeClass.LAMB_WEANER,
      startDate: weaningDate.toDate(),
      endDate: hoggetStart.toDate(),
      ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Weaner wether, typically sold by 12-18 months',
    })

    const wether24M = birthDate.add(24, 'months')

    progression.push({
      ageClass: SheepAgeClass.WETHER_12_24M,
      startDate: hoggetStart.toDate(),
      endDate: wether24M.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: 24,
      note: 'Hogget wether, prime finishing age',
    })

    progression.push({
      ageClass: SheepAgeClass.WETHER_24M_PLUS,
      startDate: wether24M.toDate(),
      endDate: null,
      ageInMonthsAtStart: 24,
      ageInMonthsAtEnd: null,
      note: 'Older wether (uncommon, usually sold earlier)',
    })
  } else {
    // Rams
    const hoggetStart = birthDate.add(12, 'months')

    progression.push({
      ageClass: SheepAgeClass.LAMB_WEANER,
      startDate: weaningDate.toDate(),
      endDate: hoggetStart.toDate(),
      ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
      ageInMonthsAtEnd: 12,
      note: 'Ram lamb, potential breeding stock',
    })

    progression.push({
      ageClass: SheepAgeClass.RAM_BREEDING,
      startDate: hoggetStart.toDate(),
      endDate: null,
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Breeding ram',
    })
  }

  return progression
}

function computeSheepFemaleProgression(
  sheep: SheepMobT,
  progression: AgeClassPeriod<SheepAgeClass>[],
  weaningDate: dayjs.Dayjs,
  events: LifeCycleEvents,
): AgeClassPeriod<SheepAgeClass>[] {
  const birthDate = dayjs(sheep.birthDate)
  const joiningAge = sheep.systemDetails.joiningAgeInMonths
  const gestationMonths = 5 // Sheep gestation

  const joiningDate = events.firstJoined
    ? dayjs(events.firstJoined)
    : birthDate.add(joiningAge, 'months')

  // LAMB_WEANER to joining or 12 months
  const weanerEnd = joiningDate.isBefore(birthDate.add(12, 'months'))
    ? joiningDate
    : birthDate.add(12, 'months')

  progression.push({
    ageClass: SheepAgeClass.LAMB_WEANER,
    startDate: weaningDate.toDate(),
    endDate: weanerEnd.toDate(),
    ageInMonthsAtStart: sheep.systemDetails.weaningAgeInMonths,
    ageInMonthsAtEnd: weanerEnd.diff(birthDate, 'months'),
    note: joiningAge < 12 ? 'Growing to early joining' : 'Weaner ewe lamb',
  })

  // If production purpose, stop here or at hogget
  if (sheep.purpose === 'selling') {
    progression.push({
      ageClass: SheepAgeClass.HOGGET,
      startDate: weanerEnd.toDate(),
      endDate: null, // Continues until sale
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: null,
      note: 'Growing for sale, not for breeding',
    })
    return progression
  }

  const firstLambingDate = joiningDate.add(gestationMonths, 'months')

  // If joining after 12 months, add HOGGET or MAIDEN_EWE
  if (joiningAge >= 12) {
    const maidenEnd = joiningDate

    progression.push({
      ageClass: joiningAge >= 18 ? SheepAgeClass.MAIDEN_EWE : SheepAgeClass.HOGGET,
      startDate: birthDate.add(12, 'months').toDate(),
      endDate: maidenEnd.toDate(),
      ageInMonthsAtStart: 12,
      ageInMonthsAtEnd: maidenEnd.diff(birthDate, 'months'),
      note: `Growing to joining age (${joiningAge} months)`,
    })

    // ADD THIS: Pregnant hogget/maiden period from joining to first lambing
    progression.push({
      ageClass: joiningAge >= 18 ? SheepAgeClass.MAIDEN_EWE : SheepAgeClass.HOGGET,
      startDate: joiningDate.toDate(),
      endDate: firstLambingDate.toDate(),
      ageInMonthsAtStart: joiningDate.diff(birthDate, 'months'),
      ageInMonthsAtEnd: firstLambingDate.diff(birthDate, 'months'),
      note: 'Pregnant with first lamb',
    })
  }

  const secondLambingDate = firstLambingDate.add(12, 'months') // Annual lambing

  // EWE_FIRST_LAMB: First lambing to second lambing
  progression.push({
    ageClass: SheepAgeClass.EWE_FIRST_LAMB,
    startDate: firstLambingDate.toDate(),
    endDate: secondLambingDate.toDate(),
    ageInMonthsAtStart: firstLambingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: secondLambingDate.diff(birthDate, 'months'),
    note: 'First-time mother, still growing',
  })

  // EWE_MATURE: After second lambing
  progression.push({
    ageClass: SheepAgeClass.EWE_MATURE,
    startDate: secondLambingDate.toDate(),
    endDate: null,
    ageInMonthsAtStart: secondLambingDate.diff(birthDate, 'months'),
    ageInMonthsAtEnd: null,
    note: 'Mature breeding ewe',
  })

  return progression
}

// Helper function to get age class at specific date (uses the progression)
export function computeAgeClass(mob: MobT, atDate: Date): CattleAgeClass | SheepAgeClass {
  const targetDate = dayjs(atDate)

  for (const period of mob.expectedAgeClassProgression) {
    const start = dayjs(period.startDate)
    const end = period.endDate ? dayjs(period.endDate) : null

    if (
      (targetDate.isSame(start, 'week') || targetDate.isAfter(start)) &&
      targetDate.isBefore(end)
    ) {
      return period.ageClass
    }
  }

  // Fallback (shouldn't reach here)
  return mob.expectedAgeClassProgression.at(mob.expectedAgeClassProgression.length - 1)!.ageClass
}

export function isInFinalAgeClass(mob: MobT, date: Date): boolean {
  const targetDate = dayjs(date)

  for (const period of mob.expectedAgeClassProgression) {
    const start = dayjs(period.startDate)
    const end = period.endDate ? dayjs(period.endDate) : null

    if (
      (targetDate.isSame(start, 'week') || targetDate.isAfter(start)) &&
      (end === null || targetDate.isBefore(end))
    ) {
      return period.endDate === null
    }
  }

  return false
}

export type GrowthCurvePoint = {
  weight: number
  date: Date
  isActual?: boolean
  source?: 'birth' | 'weighing' | 'projection'
  variance?: number // How far off was our projection from actual weighing
}

export function computeProjectedGrowthCurve(
  mob: MobT,
  targetDate: Date,
  marketCondition?: MarketCondition,
): GrowthCurvePoint[] {
  if (!marketCondition || marketCondition === 'average') {
    const cached = getCachedAverageGrowthCurve(mob, targetDate)
    if (cached) return cached
  }
  const weighingEvents = mob.eventHistory.weighingEvents || []
  const sortedWeighings = [...weighingEvents]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .filter((w) => new Date(w.date) <= targetDate)

  // PASS 1: Pure projection from birth
  const pureCurve = projectPureGrowth(mob, targetDate, marketCondition)

  // PASS 2: Blend in actual weighings
  const blendedCurve = blendWithActualWeighings(pureCurve, sortedWeighings)

  return blendedCurve
}

function getCachedAverageGrowthCurve(mob: MobT, targetDate: Date): GrowthCurvePoint[] | null {
  const cached = mob.projectedGrowthCurveAverage
  if (!cached || cached.length === 0) return null

  const latestWeighing = mob.eventHistory.weighingEvents.reduce<Date | null>((latest, event) => {
    if (!latest) return event.date
    return dayjs(event.date).isAfter(latest) ? event.date : latest
  }, null)
  const cachedWeighing = mob.projectedGrowthCurveAverageLatestWeighingDate
  if (cachedWeighing && latestWeighing && !dayjs(cachedWeighing).isSame(latestWeighing, 'day'))
    return null
  if (!cachedWeighing && latestWeighing) return null

  const lastPointDate = cached.at(cached.length - 1)?.date
  if (!lastPointDate || dayjs(lastPointDate).isBefore(targetDate, 'day')) return null

  return cached.filter((point) => point.date <= targetDate)
}

function projectPureGrowth(
  mob: MobT,
  targetDate: Date,
  marketCondition?: MarketCondition,
): GrowthCurvePoint[] {
  const curve: GrowthCurvePoint[] = []
  let currentWeight = mob.species === 'cattle' ? 40 : 5
  let currentDate = mob.birthDate

  curve.push({
    weight: currentWeight,
    date: new Date(currentDate),
    isActual: true,
    source: 'birth',
  })

  currentDate = dayjs(currentDate).add(1, 'day').toDate()

  while (currentDate <= targetDate) {
    const ageClass = computeAgeClass(mob, currentDate)
    const ageInMonths = dayjs(currentDate).diff(mob.birthDate, 'months')

    const dailyGrowth = calculateDailyGrowth(
      ageClass,
      currentWeight,
      currentDate,
      ageInMonths,
      mob.growthCurveConfig,
      mob.seasonalConfig,
      marketCondition,
    )

    currentWeight += dailyGrowth

    curve.push({
      weight: currentWeight,
      date: new Date(currentDate),
      isActual: false,
      source: 'projection',
    })

    currentDate = dayjs(currentDate).add(1, 'day').toDate()
  }

  return curve
}

function blendWithActualWeighings(
  curve: GrowthCurvePoint[],
  weighings: WeighingEvent[],
): GrowthCurvePoint[] {
  if (weighings.length === 0) return curve

  const blendedCurve = [...curve]
  const blendWindow = 180 // Days before/after weighing to blend

  for (const weighing of weighings) {
    // Find the index of this weighing date in curve
    const weighingIndex = curve.findIndex((p) => {
      const pDate = dayjs(p.date)
      const wDate = dayjs(weighing.date)
      return pDate.isSame(wDate, 'day')
    })

    if (weighingIndex === -1) continue

    // Replace with actual weighing
    const projectedWeight = curve[weighingIndex]!.weight
    const variance = weighing.averageWeight - projectedWeight

    blendedCurve[weighingIndex] = {
      weight: weighing.averageWeight,
      date: new Date(weighing.date),
      isActual: true,
      source: 'weighing',
      variance: variance,
    }

    // Blend surrounding points
    for (
      let i = Math.max(0, weighingIndex - blendWindow);
      i < Math.min(curve.length, weighingIndex + blendWindow);
      i++
    ) {
      if (i === weighingIndex) continue

      // Calculate blend factor (1.0 at weighing, 0.0 at edge of window)
      const distance = Math.abs(i - weighingIndex)
      const blendFactor = 1 - distance / blendWindow

      const point = curve[i]!
      const projected = point.weight
      const adjusted = projected + variance * blendFactor

      blendedCurve[i] = {
        ...point,
        weight: adjusted,
      }
    }
  }

  return blendedCurve
}

// Low-level helper for adding a historical headcount event to a hydrated projection. Persisted
// commands update livestock_mobs authoritatively before the event is exposed to consumers.
export function recordMobHeadcountEvent(options: {
  mob: MobT
  eventId: string
  date: Date
  headcountChange: number
  type: LivestockEventTypeT
}): void {
  options.mob.eventHistory.headcountEvents.push({
    eventId: options.eventId,
    date: options.date,
    headcountChange: options.headcountChange,
    type: options.type,
  })
}

// Moves a mob to a new paddock - a location-only fact, no headcount change.
export function recordMobLocationTransfer(
  mob: MobT,
  options: { eventId: string; date: Date; paddockId: string },
): void {
  mob.locationEvents.push({
    eventId: options.eventId,
    date: options.date,
    paddockId: options.paddockId,
    type: 'location-transfer',
  })
}
